import { useMutation } from "@apollo/client"
import cn from "classnames"
import React, { useCallback, useEffect, useState } from "react"
import { graphql } from "@/api"

type Model = "signTag" | "example" | "signCategory" | "bundle"
type Fields = "title" | "subtitle" | "examplesTitle" | "pagename" | "relatedPagesTitle" | "heading" | "contentTitle"

const signTagUpdateDoc = graphql(`
  mutation SignTagUpdate(
    $id: ID!
    $pagename: String
    $subtitle: String
    $examplesTitle: String
    $contentTitle: String
  ) {
    signTagUpdate(
      id: $id
      pagename: $pagename
      subtitle: $subtitle
      examplesTitle: $examplesTitle
      contentTitle: $contentTitle
    ) {
      signTag {
        id
        pagename
        subtitle
        examplesTitle
        contentTitle
      }
    }
  }
`)

const signCategoryUpdateDoc = graphql(`
  mutation SignCategoryUpdate($id: ID!, $title: String, $subtitle: String, $relatedPagesTitle: String) {
    signCategoryUpdate(id: $id, title: $title, subtitle: $subtitle, relatedPagesTitle: $relatedPagesTitle) {
      signCategory {
        id
        title
        subtitle
        relatedPagesTitle
      }
    }
  }
`)

const exampleUpdateDoc = graphql(`
  mutation ExampleUpdate($id: ID!, $heading: String) {
    exampleUpdate(id: $id, heading: $heading) {
      example {
        id
        heading
      }
    }
  }
`)

const bundleUpdateDoc = graphql(`
  mutation BundleInlineEdit($id: ID!, $name: String) {
    bundleInlineEdit(id: $id, name: $name) {
      bundle {
        id
        name
      }
    }
  }
`)

const mutation = (model: Model) => {
  switch (model) {
    case "signTag":
      return signTagUpdateDoc
    case "example":
      return exampleUpdateDoc
    case "signCategory":
      return signCategoryUpdateDoc
    case "bundle":
      return bundleUpdateDoc
    default:
      throw new Error(`Invalid model: ${model}`)
  }
}

export interface InlineEditProps {
  id: string
  content: string
  model: Model
  field: Fields
}

const InlineEdit: React.FC<InlineEditProps> = ({ id, content, model, field }) => {
  const [loaded, setLoaded] = useState(false)
  const [update, result] = useMutation(mutation(model))

  useEffect(() => {
    setLoaded(true)
  }, [])

  const textUpdate = useCallback(
    async (e: React.FocusEvent<HTMLSpanElement>) => {
      try {
        await update({ variables: { id, [field]: e.target.innerText } })
      } catch (error) {
        console.error(error.message)
        alert("Error saving the text. Make sure that you are logged in to admin.")
      }
    },
    [update, id, field]
  )

  return (
    <>
      <span
        className={cn("inline-edit min-h-[1em] focus-within:border-transparent", {
          "pointer-events-none cursor-not-allowed opacity-50": result.loading,
          "border-b-2 border-dashed border-primary": loaded,
        })}
        contentEditable
        onBlur={textUpdate}
        suppressContentEditableWarning
      >
        {content}
      </span>
    </>
  )
}

export default InlineEdit
